import { LibraryIcon } from "@heroicons/react/outline";

import { useSubscriptionActions } from "./hooks/useSubscriptionActions";
import { PlanActions } from "./PlanActions";
import { PlanCardLoading } from "./PlanCardLoading";
import { PlanCardDateDisplay } from "./PlanCardDateDisplay";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { TrialPill } from "@/components/ui/SubscriptionStatusBanner/TrialPill";
import { mapStripeSubscriptionStatusToStatus, useOrganizationSubscriptionData } from "@/providers";
import { capitalize } from "@/utils/strings";
import {
  getPaymentIssueMessage,
  getPaymentIssueAction,
  getPaymentIssueButtonLabel,
} from "@/utils/subscriptionStatus";
import { Button } from "@/components/ui/button";
import { useCreateCustomerPortal, useInvoicePaymentLink } from "@/queries/organizations";
import LoadingSpinner from "@/components/LoadingSpinner";

export const PlanCard = () => {
  const {
    subscriptionStatus,
    isLoadingSubscriptionStatus,
    planName,
    isTrialPlan,
    isFreePlan,
    isNewEnterprisePlan,
    isCanceled,
    hasStripe,
    hasLegacySubscription,
    isScheduledPlanChange,
    canceledAt,
    scheduledPlanType,
    isPLAdmin,
    timeUntilCancellation,
    timeUntilPlanChange,
    hasPaymentIssue,
    status,
    currentPeriodStart,
    currentPeriodEnd,
  } = useOrganizationSubscriptionData();

  const {
    cancelSubscription,
    isLoadingCancelSubscription,
    handleManageBilling,
    isLoadingCreateCustomerPortal,
    showPricingModal,
    setShowPricingModal,
  } = useSubscriptionActions({
    canceledAt,
    isCanceled,
  });

  const { mutate: createCustomerPortal, isLoading: isLoadingPortal } = useCreateCustomerPortal();
  const { mutate: getInvoicePaymentLink, isLoading: isLoadingInvoice } = useInvoicePaymentLink();

  const isLoadingPaymentAction = isLoadingPortal || isLoadingInvoice;

  const isLegacy = planName?.toLocaleLowerCase()?.includes("legacy");

  if (isLoadingSubscriptionStatus) {
    return <PlanCardLoading />;
  }

  return (
    <div className="max-w-[600px] rounded-2xl border border-gray-200 bg-white">
      <div className="px-6 py-5">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                <LibraryIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-semibold text-gray-900">Your Plan</span>
                <p className="text-sm text-gray-500">Current subscription details</p>
              </div>
            </div>
            {!isPLAdmin && (
              <PlanActions
                isLoadingSubscriptionStatus={isLoadingSubscriptionStatus}
                isCanceled={isCanceled}
                isLegacy={isLegacy}
                subscriptionStatus={subscriptionStatus}
                canceledAt={canceledAt ?? undefined}
                isScheduledPlanChange={isScheduledPlanChange ?? false}
                isFreePlan={isFreePlan}
                hasStripe={hasStripe}
                showPricingModal={showPricingModal}
                setShowPricingModal={setShowPricingModal}
                cancelSubscription={cancelSubscription}
                isLoadingCancelSubscription={isLoadingCancelSubscription}
                handleManageBilling={handleManageBilling}
                isLoadingCreateCustomerPortal={isLoadingCreateCustomerPortal}
                isNewEnterprisePlan={isNewEnterprisePlan}
              />
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {!hasLegacySubscription && (
                <Badge
                  variant={isFreePlan ? BadgeVariant.GRAY : BadgeVariant.BLUE}
                  text={
                    isTrialPlan || !planName
                      ? `${capitalize(planName!)} Trial`
                      : capitalize(planName!)
                  }
                />
              )}

              {isTrialPlan && <TrialPill />}

              {!isFreePlan && !isTrialPlan && !canceledAt && !isScheduledPlanChange && (
                <Badge
                  variant={
                    status === "active" || status === "trialing" || !status
                      ? BadgeVariant.GREEN
                      : BadgeVariant.ORANGE
                  }
                  text={mapStripeSubscriptionStatusToStatus(status ?? "") || "Active"}
                />
              )}

              {canceledAt &&
                !isCanceled &&
                timeUntilCancellation !== null &&
                timeUntilCancellation.value > 0 && (
                  <Badge
                    variant={BadgeVariant.ORANGE}
                    text={`Cancels in ${timeUntilCancellation.displayText}`}
                  />
                )}

              {isScheduledPlanChange &&
                scheduledPlanType &&
                timeUntilPlanChange !== null &&
                timeUntilPlanChange.value >= 0 && (
                  <Badge
                    variant={BadgeVariant.PURPLE}
                    text={`Switches to ${scheduledPlanType} in ${timeUntilPlanChange.displayText}`}
                  />
                )}
            </div>

            <PlanCardDateDisplay
              currentPeriodStart={currentPeriodStart}
              currentPeriodEnd={currentPeriodEnd}
              isNewEnterprisePlan={isNewEnterprisePlan}
            />
          </div>

          {canceledAt &&
            !isCanceled &&
            timeUntilCancellation !== null &&
            timeUntilCancellation.value > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-sm text-orange-800">
                  Your subscription will be canceled in {timeUntilCancellation.displayText}.
                  <span className="font-medium"> Renew the subscription</span> to continue using
                  premium features.
                </p>
              </div>
            )}

          {hasPaymentIssue && status && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-orange-800">{getPaymentIssueMessage(status)}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const action = getPaymentIssueAction(status);
                    if (action === "pay_invoice") {
                      getInvoicePaymentLink();
                    } else if (action === "update_payment_method") {
                      createCustomerPortal({ return_url: window.location.href });
                    }
                  }}
                  disabled={isLoadingPaymentAction}
                  className="flex-shrink-0 border-orange-600 bg-transparent text-orange-600 hover:border-orange-700 hover:bg-orange-100 hover:text-orange-700"
                >
                  {isLoadingPaymentAction ? (
                    <>
                      <LoadingSpinner size={4} />
                      <span className="ml-2">Loading...</span>
                    </>
                  ) : (
                    getPaymentIssueButtonLabel(status)
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
